import { createRootRoute, createRoute, Outlet, redirect } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { getToken, isExpired } from "@/lib/auth";
import { AppLayout } from "@/components/AppLayout";
import { LoginPage } from "@/routes/LoginPage";
import { DashboardPage } from "@/routes/DashboardPage";
import { ExpensesPage } from "@/routes/ExpensesPage";
import { AccountsPage } from "@/routes/AccountsPage";
import { SnapshotsPage } from "@/routes/SnapshotsPage";
import { IncomesPage } from "@/routes/IncomesPage";
import { GoalsPage } from "@/routes/GoalsPage";
import { GoalDetailPage } from "@/routes/GoalDetailPage";
import { TransactionsPage } from "@/routes/TransactionsPage";
import { CategoriesPage } from "@/routes/CategoriesPage";
import { BudgetsPage } from "@/routes/BudgetsPage";
import { PlanPage } from "@/routes/PlanPage";
import { InvestmentsPage } from "@/routes/InvestmentsPage";
import { SettingsPage } from "@/routes/SettingsPage";
import { NoteCleanupPage } from "@/routes/NoteCleanupPage";

interface RouterContext { queryClient: QueryClient }

const rootRoute = createRootRoute<RouterContext>({
    component: () => <Outlet />,
});

const loginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/login",
    component: LoginPage,
});

const authedRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: "authed",
    beforeLoad: ({ location }) => {
        const token = getToken();
        if (!token || isExpired(token)) {
            // ADM-03 (SPEC-044): запоминаем куда шли — LoginPage передаст path
            // в return_to, после логина OAuth вернёт на этот же URL.
            try { sessionStorage.setItem("admin.return_to", location.href); } catch { /* ignore */ }
            throw redirect({ to: "/login" });
        }
    },
    component: AppLayout,
});

const indexRoute = createRoute({
    getParentRoute: () => authedRoute,
    path: "/",
    component: DashboardPage,
});

const expensesRoute = createRoute({
    getParentRoute: () => authedRoute,
    path: "/expenses",
    component: ExpensesPage,
});

const accountsRoute = createRoute({
    getParentRoute: () => authedRoute,
    path: "/accounts",
    component: AccountsPage,
});

const snapshotsRoute = createRoute({
    getParentRoute: () => authedRoute,
    path: "/snapshots",
    // ADM-10: карточка ведра на /accounts ведёт сюда с ?account_id=… — схема search
    // валидируется явно (раньше параметр игнорировался, а Link маскировал типы `as any`).
    validateSearch: (search: Record<string, unknown>): { account_id?: string } => ({
        account_id: typeof search.account_id === "string" && search.account_id !== "" ? search.account_id : undefined,
    }),
    component: SnapshotsPage,
});

const incomesRoute = createRoute({
    getParentRoute: () => authedRoute,
    path: "/incomes",
    component: IncomesPage,
});

const goalsRoute = createRoute({
    getParentRoute: () => authedRoute,
    path: "/goals",
    component: GoalsPage,
});

const goalDetailRoute = createRoute({
    getParentRoute: () => authedRoute,
    path: "/goals/$goalId",
    component: GoalDetailPage,
});

const transactionsRoute = createRoute({
    getParentRoute: () => authedRoute,
    path: "/transactions",
    component: TransactionsPage,
});

const categoriesRoute = createRoute({
    getParentRoute: () => authedRoute,
    path: "/categories",
    component: CategoriesPage,
});

const planRoute = createRoute({
    getParentRoute: () => authedRoute,
    path: "/plan",
    component: PlanPage,
});

const budgetsRoute = createRoute({
    getParentRoute: () => authedRoute,
    path: "/budgets",
    component: BudgetsPage,
});

const investmentsRoute = createRoute({
    getParentRoute: () => authedRoute,
    path: "/investments",
    component: InvestmentsPage,
});

const settingsRoute = createRoute({
    getParentRoute: () => authedRoute,
    path: "/settings",
    component: SettingsPage,
});

// SPEC-055: чистка комментариев — обслуживание данных, поэтому живёт под настройками,
// а не отдельным пунктом меню: заходят сюда раз в несколько месяцев.
const noteCleanupRoute = createRoute({
    getParentRoute: () => authedRoute,
    path: "/settings/note-cleanup",
    component: NoteCleanupPage,
});

export const routeTree = rootRoute.addChildren([
    loginRoute,
    authedRoute.addChildren([
        indexRoute, accountsRoute, snapshotsRoute, expensesRoute,
        incomesRoute, goalsRoute, goalDetailRoute,
        transactionsRoute, categoriesRoute, planRoute, budgetsRoute, investmentsRoute,
        settingsRoute, noteCleanupRoute,
    ]),
]);
